import { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setCredentials } from "../../../redux/slices/authSlice";
import {
  useLoginMutation,
  useGoogleLoginMutation,
} from "../../../redux/slices/api/authApiSlice";
import "../styles/Login.css";
import Button from "../../shared/Button";

// `asModal` renders just the card (no full-screen container / route Navigate).
// `onClose` fires after a successful login; `onSwitch` swaps to the signup form.
const Login = ({ asModal = false, onClose, onSwitch }) => {
  const [credentials, setCredential] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [loginApi, { isLoading }] = useLoginMutation();
  const [googleLoginApi] = useGoogleLoginMutation();

  const [error, setError] = useState("");

  useEffect(() => {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
      });

      window.google.accounts.id.renderButton(
        document.getElementById("googleSignInDiv"),
        {
          theme: "outline",
          size: "large",
          width: 260,
        }
      );
    }
  }, []);

  const handleGoogleResponse = async (response) => {
    try {
      const res = await googleLoginApi({
        token: response.credential,
      }).unwrap();
      dispatch(setCredentials(res));
      if (asModal && onClose) onClose();
    } catch (err) {
      setError("Google login failed");
    }
  };

  const handleInputChange = (e) => {
    setCredential({
      ...credentials,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await loginApi(credentials).unwrap();
      dispatch(setCredentials(res));
      if (asModal && onClose) onClose();
    } catch (err) {
      setError(err?.data?.message || "Login failed");
    }
  };

  // Full-page mode only: bounce an already-logged-in user away.
  if (!asModal && user) {
    return <Navigate to="/homepage" replace />;
  }

  const card = (
    <div className="auth-card">
        <div className="auth-header">
          <h2 className="auth-title">Welcome Back</h2>
          <p className="auth-subtitle">Sign in to your DS Visualizer account</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="input-wrapper">
            <label htmlFor="email" className="input-label">Email Address</label>
            <input
              id="email"
              type="email"
              name="email"
              placeholder="you@example.com"
              value={credentials.email}
              onChange={handleInputChange}
              onFocus={() => setFocusedField("email")}
              onBlur={() => setFocusedField(null)}
              className={focusedField === "email" ? "focused" : ""}
              required
            />
            <div className="input-underline"></div>
          </div>

          <div className="input-wrapper">
            <label htmlFor="password" className="input-label">Password</label>
            <div className="password-field">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="••••••••"
                value={credentials.password}
                onChange={handleInputChange}
                onFocus={() => setFocusedField("password")}
                onBlur={() => setFocusedField(null)}
                className={focusedField === "password" ? "focused" : ""}
                required
              />
              <Button
                type="button"
                variant=""
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "Hide" : "Show"}
              </Button>
            </div>
            <div className="input-underline"></div>
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠</span> {error}
            </div>
          )}

          <Button type="submit" variant="" disabled={isLoading} className="auth-button">
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Logging in...
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </form>

        <div className="divider">
          <span>OR</span>
        </div>

        <div className="google-container">
          <div id="googleSignInDiv"></div>
        </div>

        <p className="auth-footer">
          Don't have an account?{" "}
          {asModal ? (
            <button type="button" className="auth-link auth-link-button" onClick={onSwitch}>
              Create one
            </button>
          ) : (
            <Link to="/signup" className="auth-link">
              Create one
            </Link>
          )}
        </p>
      </div>
  );

  if (asModal) {
    return card;
  }

  return (
    <div className="auth-container">
      <div className="gradient-bg"></div>
      {card}
    </div>
  );
};

export default Login;
